/**
 * Sync Engine — Queues offline mutations and syncs when back online
 *
 * Flow:
 *  1. When offline, mutations go to sync_queue in IndexedDB
 *  2. When online, drain the queue and replay against Supabase
 *  3. Conflicts resolved by "last-write-wins" (server timestamp)
 */
import { create } from 'zustand';
import {
  putRecord,
  getAllRecords,
  deleteRecord,
  putManyRecords,
  type StoreName,
} from './storage';
import { supabase } from '../supabase';
import { getErrorMessage } from '@/shared/utils/errors';
import { createLogger } from '@/shared/utils/logger';
import { safeUUID } from '@/shared/utils/uuid';

const syncLog = createLogger('SyncEngine');

// ===== Sync Queue Item =====

export interface SyncQueueItem {
  id: string;
  table: string;
  operation: 'insert' | 'update' | 'delete';
  payload: Record<string, unknown>;
  recordId: string;
  createdAt: string;
  retries: number;
}

// ===== Offline Store (Zustand) =====

interface OfflineState {
  isOnline: boolean;
  syncInProgress: boolean;
  pendingCount: number;
  lastSyncAt: string | null;
  lastError: string | null;

  setOnline: (v: boolean) => void;
  setSyncInProgress: (v: boolean) => void;
  setPendingCount: (v: number) => void;
  setLastSync: (v: string) => void;
  setLastError: (v: string | null) => void;
}

export const useOfflineStore = create<OfflineState>((set) => ({
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  syncInProgress: false,
  pendingCount: 0,
  lastSyncAt: null,
  lastError: null,

  setOnline: (v) => set({ isOnline: v }),
  setSyncInProgress: (v) => set({ syncInProgress: v }),
  setPendingCount: (v) => set({ pendingCount: v }),
  setLastSync: (v) => set({ lastSyncAt: v, lastError: null }),
  setLastError: (v) => set({ lastError: v }),
}));

// ===== Enqueue an offline mutation =====

/** @internal reserved for future offline integration */
export async function enqueueMutation(
  table: string,
  operation: 'insert' | 'update' | 'delete',
  recordId: string,
  payload: Record<string, unknown>,
): Promise<void> {
  const item: SyncQueueItem = {
    id: safeUUID(),
    table,
    operation,
    payload,
    recordId,
    createdAt: new Date().toISOString(),
    retries: 0,
  };
  await putRecord('sync_queue', item);
  // Count only non-DLQ items as "pending" (consistent with syncQueueInternal)
  const all = await getAllRecords<SyncQueueItem & { _deadLetter?: boolean }>('sync_queue');
  useOfflineStore.getState().setPendingCount(all.filter((i) => !i._deadLetter).length);
}

// ===== Drain queue and sync =====

/** Promise-based mutex: prevents concurrent syncQueue executions even with microtask interleaving */
let syncMutex: Promise<{ synced: number; failed: number }> | null = null;

export function syncQueue(): Promise<{ synced: number; failed: number }> {
  if (syncMutex) return syncMutex;
  syncMutex = syncQueueInternal().finally(() => { syncMutex = null; });
  return syncMutex;
}

async function syncQueueInternal(): Promise<{ synced: number; failed: number }> {
  const store = useOfflineStore.getState();
  if (!store.isOnline) return { synced: 0, failed: 0 };

  store.setSyncInProgress(true);
  let synced = 0;
  let failed = 0;

  try {
    const allItems = await getAllRecords<SyncQueueItem & { _deadLetter?: boolean }>('sync_queue');
    // Filter out dead-letter items — they've exhausted retries and should not be re-processed
    const items = allItems.filter((item) => !item._deadLetter);
    // Process in order of creation (copy to avoid mutating the source array)
    const sorted = [...items].sort((a, b) => a.createdAt.localeCompare(b.createdAt));

    for (const item of sorted) {
      try {
        // Dynamic table dispatch: sync queue stores arbitrary table payloads as JSON.
        // Supabase's typed client requires a known table literal, so we cast through 'novels'.
        // The `as never` on payloads is intentional — the data shape is only known at runtime.
        const from = (t: string) => supabase.from(t as 'novels');
        if (item.operation === 'insert') {
          const { error } = await from(item.table).insert(item.payload as never);
          if (error) throw error;
        } else if (item.operation === 'update') {
          const { id: _, ...updates } = item.payload;
          const { error } = await from(item.table)
            .update(updates as never)
            .eq('id', item.recordId);
          if (error) throw error;
        } else if (item.operation === 'delete') {
          const { error } = await from(item.table).delete().eq('id', item.recordId);
          if (error) throw error;
        }

        await deleteRecord('sync_queue', item.id);
        synced++;
      } catch (err) {
        failed++;
        // Increment retry count
        item.retries++;
        if (item.retries < 5) {
          await putRecord('sync_queue', item);
        } else {
          // Move to dead-letter queue instead of silently deleting
          try {
            await putRecord('sync_queue', {
              ...item,
              id: `dlq_${item.id}`,
              _deadLetter: true,
              _failedAt: new Date().toISOString(),
              _lastError: getErrorMessage(err),
            } as SyncQueueItem & Record<string, unknown>);
          } catch (dlqErr) {
            syncLog.warn('DLQ write failed — keeping original item to prevent data loss', { error: String(dlqErr) });
            continue; // skip deleteRecord — original item stays in queue for possible manual recovery
          }
          await deleteRecord('sync_queue', item.id);
          syncLog.error(`Moved item ${item.id} to dead-letter queue after 5 retries`, { error: String(err) });
          // Notify UI via store
          store.setLastError(`同步失败: ${item.table} ${item.operation} — 数据已保留在本地,请稍后重试`);
        }
      }
    }

    store.setLastSync(new Date().toISOString());
    // Count only non-DLQ items as "pending"
    const remaining = await getAllRecords<SyncQueueItem & { _deadLetter?: boolean }>('sync_queue');
    store.setPendingCount(
      remaining.filter((item) => !item._deadLetter).length,
    );
  } catch (err) {
    store.setLastError(getErrorMessage(err));
  } finally {
    store.setSyncInProgress(false);
  }

  return { synced, failed };
}

// ===== Cache Supabase data to IndexedDB =====

/** @internal reserved for future offline integration */
export async function cacheTableData<T extends { id: string }>(
  storeName: StoreName,
  data: T[],
): Promise<void> {
  await putManyRecords(storeName, data);
}

// ===== Initialize online/offline listeners =====

export function initOfflineListeners(): () => void {
  const store = useOfflineStore.getState();

  const handleOnline = () => {
    store.setOnline(true);
    // Auto-sync when coming back online
    syncQueue().catch((err) => syncLog.error('Auto-sync failed on reconnect', { error: getErrorMessage(err) }));
  };
  const handleOffline = () => {
    store.setOnline(false);
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Initial sync check
  if (navigator.onLine) {
    syncQueue().catch((err) => syncLog.error('Initial sync check failed', { error: getErrorMessage(err) }));
  }

  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}